import { useRef, useEffect, useState } from 'react'
import { AdminTableProps } from '../../models'
import { useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsAdminSearch,
  selectedLmsLocationSearch,
  selectedLmsStatus,
} from '../../lmsSlice'
import { getWardResults } from '../../utils'
import { create, update, menuValues } from '../../constants'
import { allocation } from '../../../bedManagement/modals'
import ProvenanceView from '../../../../components/ProvenanceView'
import { Box, IconButton, useTheme } from '@mui/material'
import { SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import KeycloakService from '../../../../utils/keycloakService'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import '../../../administration/admin.scss'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import { Archive } from './Archive'
import {
  activeText,
  inactiveText,
} from '../../../Radiology/Constants/constants'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { radiologyColor } from '../../../Radiology/Utils/RadiologyColors'
import { ColDef } from 'ag-grid-community'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
import { loading } from '../../../appointments/models'

const styles = () => ({
  centerHeader: {
    color: radiologyColor?.white,
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
    },
  },
  nameButton: {
    backgroundColor: '#0000',
    border: 'none',
    color: 'blue',
    cursor: 'pointer',
    padding: '0px',
    textAlign: 'left',
    textDecoration: 'underline',
  },
})
const Wards = ({ data, onEdit }: AdminTableProps) => {
  const searchParam = useAppSelector(selectedLmsAdminSearch)
  const locationSearch = useAppSelector(selectedLmsLocationSearch)
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [tableData, setTableData] = useState<any>(data)
  const [wardData, setWardData] = useState<any>({})
  const status = useAppSelector(selectedLmsStatus)
  let filtered = getWardResults(tableData, searchParam, locationSearch)
  filtered = filtered?.filter((d: any) => {
    const wardStatus = d?.active ? activeText : inactiveText
    return status === wardStatus
  })
  const sampleTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = sampleTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])

  function handleDeleteDialogOpen(d: any) {
    setDeleteDialog(true)
    setWardData(d)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setWardData(null)
  }

  useEffect(() => {
    setTableData(data)
  }, [data])
  const RenderActions = (params: any) => {
    return (
      <div className='set-action-icons'>
        {params?.data?.active ? (
          <IconButton
            title='Archive'
            size='small'
            onClick={() =>
              Archive(params?.data, setTableData, menuValues?.Ward)
            }
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <ArchiveOutlinedIcon fontSize='small' />
          </IconButton>
        ) : (
          <IconButton
            title='Un-Archive'
            size='small'
            onClick={() =>
              Archive(params?.data, setTableData, menuValues?.Ward)
            }
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <UnarchiveOutlinedIcon fontSize='small' />
          </IconButton>
        )}
        <IconButton
          title='Delete'
          size='small'
          onClick={() => handleDeleteDialogOpen(params?.data)}
          disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
        >
          <DeleteOutlined fontSize='small' />
        </IconButton>
      </div>
    )
  }
  const renderWardName = (params: any) => {
    return (
      <>
        <button
          onClick={() => onEdit(params?.data?.id)}
          className={classes?.nameButton}
        >
          {params?.data?.ward_name}
        </button>
      </>
    )
  }
  const columnDefs: ColDef[] = [
    {
      headerName: 'SeqNo',
      field: 'sequence_number',
      width: 120,
      sortable: true,
      filter: true,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.sequence_number ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Ward Or Room',
      field: 'id',
      minWidth: 350,
      sortable: false,
      filter: false,
      cellRenderer: renderWardName,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'left',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 0.5,
        display: 'flex',
        alignItems: 'center',
      },
      wrapText: true,
    },
    {
      headerName: 'floor',
      field: 'floor_number',
      minWidth: 140,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) =>
        params?.data?.floor_number ?? allocation.hyphen,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Location',
      field: 'location',
      minWidth: 140,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.location_id ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Maximum Beds',
      field: 'maximum_beds',
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => params?.data?.maximum_beds ?? '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
      },
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      minWidth: 180,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        return (
          <ProvenanceView
            mode={create}
            date={params?.data?.date_created ?? ''}
            name={params?.data?.created_by ?? '-'}
          />
        )
      },
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      minWidth: 180,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => {
        return (
          <ProvenanceView
            mode={update}
            date={params?.data?.date_changed ?? ''}
            name={params?.data?.updated_by ?? '-'}
          />
        )
      },
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
    },
    {
      headerName: 'Actions',
      field: 'id',
      minWidth: 150,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: RenderActions,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
  ]
  return (
    <>
      <Box height={'100%'} width={'100%'}>
        <MedAgDataGrid
          tableHeight={'100%'}
          rowData={filtered}
          columnDefs={columnDefs}
          pagination={false}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          rowHeight={40}
          localSearch={false}
          enableCellTextSelection={true}
          loading={status === loading ? true : false}
        />
      </Box>
      {deleteDialog && (
        <DeleteConfirmationDialog
          openDialog={deleteDialog}
          closeDialog={handleCloseDeleteDialog}
          data={wardData}
          name={wardData?.ward_name}
          setData={setTableData}
          item={menuValues?.Ward}
        />
      )}
    </>
  )
}

export default Wards
